/**
 * Sign Up Page - Olympic Fit Wellness
 * Email verification flow: Enter email → Verify code → Complete profile
 */
import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { 
  Mail, Lock, Eye, EyeOff, AlertCircle, CheckCircle, 
  User, Phone, Calendar, MapPin, UserPlus, Loader2,
  ArrowRight, ArrowLeft
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function SignUpPage() {
  const { signIn, signInWithGoogle, signInWithApple, sendPhoneOTP, verifyPhoneOTP, isAuthenticated, isLoading } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // Get plan and redirect from URL params
  const selectedPlan = searchParams.get('plan');
  const redirectAfter = searchParams.get('redirect');
  
  // Auth method: email | phone
  const [authMethod, setAuthMethod] = useState('email');
  
  // Step: email | verify | profile (or phone | phone-verify | profile)
  const [step, setStep] = useState('email');
  
  // Form data
  const [email, setEmail] = useState('');
  const [phone, setPhoneNumber] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    password: '',
    confirmPassword: '',
    phone: '',
    dateOfBirth: '',
    street: '',
    city: '',
    state: '',
    zipCode: '',
    emergencyName: '',
    emergencyPhone: '',
    emergencyRelationship: ''
  });
  
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [codeSent, setCodeSent] = useState(false);

  useEffect(() => {
    if (isAuthenticated && !isLoading) {
      navigate('/portal');
    }
  }, [isAuthenticated, isLoading, navigate]);

  const handleSendVerificationCode = async (e) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);

    try {
      const response = await fetch(`${API_URL}/api/auth/send-verification`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Failed to send verification code');
      }

      setCodeSent(true);
      setStep('verify');
    } catch (err) {
      setError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleVerifyCode = async (e) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);

    try {
      const response = await fetch(`${API_URL}/api/auth/verify-code`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, code: verificationCode })
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Invalid verification code');
      }

      setStep('profile');
    } catch (err) {
      setError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCompleteSignup = async (e) => {
    e.preventDefault();
    setError('');

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch(`${API_URL}/api/auth/complete-signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          password: formData.password,
          first_name: formData.firstName,
          last_name: formData.lastName,
          phone: formData.phone || null,
          date_of_birth: formData.dateOfBirth || null,
          address: formData.street ? {
            street: formData.street,
            city: formData.city,
            state: formData.state,
            zip_code: formData.zipCode
          } : null,
          emergency_contact: formData.emergencyName ? {
            name: formData.emergencyName,
            phone: formData.emergencyPhone,
            relationship: formData.emergencyRelationship
          } : null
        })
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Failed to create account');
      }

      // Auto sign in after successful signup
      const signInResult = await signIn(email, formData.password);
      if (signInResult.success) {
        // If user came from membership page with a plan, redirect back with checkout flag
        if (redirectAfter === 'membership' && selectedPlan) {
          navigate(`/membership?plan=${selectedPlan}&checkout=true`);
        } else {
          navigate('/portal');
        }
      } else {
        navigate('/login');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleGoogleSignIn = async () => {
    const result = await signInWithGoogle();
    if (!result.success) {
      setError(result.error || 'Failed to sign in with Google');
    }
  };

  const handleAppleSignIn = async () => {
    const result = await signInWithApple();
    if (!result.success) {
      setError(result.error || 'Failed to sign in with Apple');
    }
  };

  // Phone authentication handlers
  const handleSendPhoneOTP = async (e) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);

    try {
      const result = await sendPhoneOTP(phone);
      if (!result.success) {
        throw new Error(result.error || 'Failed to send verification code');
      }
      setCodeSent(true);
      setStep('phone-verify');
    } catch (err) {
      setError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleVerifyPhoneOTP = async (e) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);

    try {
      const result = await verifyPhoneOTP(phone, verificationCode);
      if (!result.success) {
        throw new Error(result.error || 'Invalid verification code');
      }
      
      // Phone auth successful - redirect
      if (redirectAfter === 'membership' && selectedPlan) {
        navigate(`/membership?plan=${selectedPlan}&checkout=true`);
      } else {
        navigate('/portal');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="w-12 h-12 border-4 border-accent-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white flex" data-testid="signup-page">
      {/* Left Side - Image */}
      <div className="hidden lg:flex lg:w-1/2 relative">
        <img 
          src="https://images.unsplash.com/photo-1534258936925-c58bed479fcb?w=1200&q=80"
          alt="Olympic Fit Wellness"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-neutral-900/80 to-neutral-900/40" />
        <div className="absolute inset-0 flex items-center justify-center p-12">
          <div className="max-w-md">
            <Link to="/" className="inline-block mb-8">
              <span className="text-4xl font-heading font-bold text-white">
                <span>O</span>
                <span>F</span>
                <span className="text-red-500">W</span>
              </span>
            </Link>
            <h1 className="text-4xl font-heading font-bold text-white mb-4">
              Join Our Community
            </h1>
            <p className="text-xl text-white/70">
              Start your wellness journey with Dallas's premier fitness destination.
            </p>
            
            {/* Step Indicator */}
            <div className="flex items-center gap-4 mt-8">
              {['email', 'verify', 'profile'].map((s, i) => (
                <div key={s} className="flex items-center">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                    step === s ? 'bg-white text-neutral-900' : 
                    ['email', 'verify', 'profile'].indexOf(step) > i ? 'bg-green-500 text-white' :
                    'bg-white/30 text-white/70'
                  }`}>
                    {['email', 'verify', 'profile'].indexOf(step) > i ? '✓' : i + 1}
                  </div>
                  {i < 2 && <div className={`w-12 h-0.5 ml-2 ${
                    ['email', 'verify', 'profile'].indexOf(step) > i ? 'bg-green-500' : 'bg-white/30'
                  }`} />}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Right Side - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 overflow-y-auto">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <Link to="/" className="lg:hidden inline-block mb-6">
            <span className="text-4xl font-heading font-bold text-neutral-900">
              <span>O</span>
              <span>F</span>
              <span className="text-red-500">W</span>
            </span>
          </Link>

          {/* Membership redirect notice */}
          {selectedPlan && redirectAfter === 'membership' && (
            <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg flex items-start gap-3 text-blue-800" data-testid="membership-redirect-notice">
              <CheckCircle className="w-5 h-5 flex-shrink-0 mt-0.5 text-blue-600" />
              <div>
                <p className="font-medium">Complete signup to purchase your membership</p>
                <p className="text-sm text-blue-600 mt-1">
                  You've selected the <span className="font-semibold capitalize">{selectedPlan}</span> plan. 
                  Create your account to proceed to checkout.
                </p>
              </div>
            </div>
          )}

          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3 text-red-700">
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
              <span className="text-sm">{error}</span>
            </div>
          )}

          {/* Step 1: Email */}
          {step === 'email' && (
            <>
              <h2 className="text-3xl font-heading font-bold text-neutral-900 mb-2">Create Account</h2>
              <p className="text-neutral-500 mb-8">
                Enter your email to get started. We'll send you a verification code.
              </p>

              <form onSubmit={handleSendVerificationCode} className="space-y-4 mb-6">
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1.5">
                    Email Address
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-400" />
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="input-field pl-10"
                      placeholder="you@example.com"
                      required
                      data-testid="email-input"
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="btn-primary w-full disabled:opacity-50 flex items-center justify-center gap-2"
                  data-testid="send-code-button"
                >
                  {isSubmitting && <Loader2 className="w-4 h-4 animate-spin" />}
                  {isSubmitting ? 'Sending...' : 'Send Verification Code'}
                </button>
              </form>

              <div className="relative mb-6">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-neutral-200" />
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-4 bg-white text-neutral-400">or continue with</span>
                </div>
              </div>

              <button
                onClick={handleGoogleSignIn}
                className="w-full flex items-center justify-center gap-3 bg-white hover:bg-neutral-50 text-neutral-700 font-medium py-3 px-6 rounded-lg border border-neutral-300 transition-colors mb-3"
                data-testid="google-signup-button"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                Continue with Google
              </button>

              <button
                onClick={handleAppleSignIn}
                className="w-full flex items-center justify-center gap-3 bg-black hover:bg-neutral-800 text-white font-medium py-3 px-6 rounded-lg transition-colors mb-3"
                data-testid="apple-signup-button"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
                </svg>
                Continue with Apple
              </button>

              <button
                onClick={() => {
                  setAuthMethod('phone');
                  setStep('phone');
                }}
                className="w-full flex items-center justify-center gap-3 bg-white hover:bg-neutral-50 text-neutral-700 font-medium py-3 px-6 rounded-lg border border-neutral-300 transition-colors mb-6"
                data-testid="phone-signup-button"
              >
                <Phone className="w-5 h-5" />
                Continue with Phone
              </button>

              <p className="text-center text-neutral-500 text-sm">
                Already have an account?{' '}
                <Link 
                  to={selectedPlan && redirectAfter === 'membership' 
                    ? `/login?plan=${selectedPlan}&redirect=membership` 
                    : '/login'
                  } 
                  className="text-accent-600 hover:underline font-medium"
                >
                  Sign in
                </Link>
              </p>
            </>
          )}

          {/* Phone Step 1: Enter Phone Number */}
          {step === 'phone' && (
            <>
              <button onClick={() => { setAuthMethod('email'); setStep('email'); }} className="flex items-center gap-2 text-neutral-500 hover:text-neutral-700 mb-6">
                <ArrowLeft className="w-4 h-4" />
                Back
              </button>

              <h2 className="text-3xl font-heading font-bold text-neutral-900 mb-2">Phone Sign Up</h2>
              <p className="text-neutral-500 mb-8">
                Enter your phone number to receive a verification code.
              </p>

              <form onSubmit={handleSendPhoneOTP} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1.5">
                    Phone Number
                  </label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-400" />
                    <input
                      type="tel"
                      value={phone}
                      onChange={(e) => setPhoneNumber(e.target.value)}
                      className="input-field pl-10"
                      placeholder="+1 (555) 123-4567"
                      required
                      data-testid="phone-input"
                    />
                  </div>
                  <p className="text-xs text-neutral-400 mt-1">
                    Include country code (e.g., +1 for US)
                  </p>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="btn-primary w-full disabled:opacity-50 flex items-center justify-center gap-2"
                  data-testid="send-phone-code-button"
                >
                  {isSubmitting && <Loader2 className="w-4 h-4 animate-spin" />}
                  {isSubmitting ? 'Sending...' : 'Send Verification Code'}
                </button>
              </form>
            </>
          )}

          {/* Phone Step 2: Verify Phone Code */}
          {step === 'phone-verify' && (
            <>
              <button onClick={() => setStep('phone')} className="flex items-center gap-2 text-neutral-500 hover:text-neutral-700 mb-6">
                <ArrowLeft className="w-4 h-4" />
                Back
              </button>

              <h2 className="text-3xl font-heading font-bold text-neutral-900 mb-2">Verify Phone</h2>
              <p className="text-neutral-500 mb-8">
                We sent a 6-digit code to <span className="font-medium text-neutral-700">{phone}</span>
              </p>

              <form onSubmit={handleVerifyPhoneOTP} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1.5">
                    Verification Code
                  </label>
                  <input
                    type="text"
                    value={verificationCode}
                    onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    className="input-field text-center text-2xl tracking-[0.5em] font-bold"
                    placeholder="000000"
                    maxLength={6}
                    required
                    autoFocus
                    data-testid="phone-verification-code-input"
                  />
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting || verificationCode.length !== 6}
                  className="btn-primary w-full disabled:opacity-50 flex items-center justify-center gap-2"
                  data-testid="verify-phone-button"
                >
                  {isSubmitting && <Loader2 className="w-4 h-4 animate-spin" />}
                  {isSubmitting ? 'Verifying...' : 'Verify & Continue'}
                  <ArrowRight className="w-4 h-4" />
                </button>

                <button
                  type="button"
                  onClick={handleSendPhoneOTP}
                  disabled={isSubmitting}
                  className="w-full text-center text-neutral-500 hover:text-neutral-700 text-sm"
                >
                  Didn't receive the code? Resend
                </button>
              </form>
            </>
          )}

          {/* Step 2: Verify Code (Email) */}
          {step === 'verify' && (
            <>
              <button onClick={() => setStep('email')} className="flex items-center gap-2 text-neutral-500 hover:text-neutral-700 mb-6">
                <ArrowLeft className="w-4 h-4" />
                Back
              </button>

              <h2 className="text-3xl font-heading font-bold text-neutral-900 mb-2">Verify Email</h2>
              <p className="text-neutral-500 mb-8">
                We sent a 6-digit code to <span className="font-medium text-neutral-700">{email}</span>
              </p>

              <form onSubmit={handleVerifyCode} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1.5">
                    Verification Code
                  </label>
                  <input
                    type="text"
                    value={verificationCode}
                    onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    className="input-field text-center text-2xl tracking-[0.5em] font-bold"
                    placeholder="000000"
                    maxLength={6}
                    required
                    autoFocus
                    data-testid="verification-code-input"
                  />
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting || verificationCode.length !== 6}
                  className="btn-primary w-full disabled:opacity-50 flex items-center justify-center gap-2"
                  data-testid="verify-button"
                >
                  {isSubmitting && <Loader2 className="w-4 h-4 animate-spin" />}
                  {isSubmitting ? 'Verifying...' : 'Verify & Continue'}
                  <ArrowRight className="w-4 h-4" />
                </button>

                <button
                  type="button"
                  onClick={handleSendVerificationCode}
                  disabled={isSubmitting}
                  className="w-full text-center text-neutral-500 hover:text-neutral-700 text-sm"
                >
                  Didn't receive the code? Resend
                </button>
              </form>
            </>
          )}

          {/* Step 3: Complete Profile */}
          {step === 'profile' && (
            <>
              <div className="flex items-center gap-2 text-green-600 mb-4">
                <CheckCircle className="w-5 h-5" />
                <span className="text-sm font-medium">Email verified!</span>
              </div>

              <h2 className="text-3xl font-heading font-bold text-neutral-900 mb-2">Complete Your Profile</h2>
              <p className="text-neutral-500 mb-6">
                Set up your account details to get started.
              </p>

              <form onSubmit={handleCompleteSignup} className="space-y-4">
                {/* Name */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-1.5">
                      First Name *
                    </label>
                    <input
                      type="text"
                      value={formData.firstName}
                      onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                      className="input-field"
                      placeholder="John"
                      required
                      data-testid="first-name-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-1.5">
                      Last Name *
                    </label>
                    <input
                      type="text"
                      value={formData.lastName}
                      onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                      className="input-field"
                      placeholder="Doe"
                      required
                      data-testid="last-name-input"
                    />
                  </div>
                </div>

                {/* Password */}
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1.5">
                    Password *
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-400" />
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      className="input-field pl-10 pr-10"
                      placeholder="Min. 8 characters"
                      minLength={8}
                      required
                      data-testid="password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-600"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1.5">
                    Confirm Password *
                  </label>
                  <input
                    type="password"
                    value={formData.confirmPassword}
                    onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                    className="input-field"
                    placeholder="Confirm password"
                    required
                    data-testid="confirm-password-input"
                  />
                </div>

                {/* Phone & DOB */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-1.5">
                      Phone
                    </label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-400" />
                      <input
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        className="input-field pl-10"
                        placeholder="(555) 123-4567"
                        data-testid="phone-input"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-1.5">
                      Date of Birth
                    </label>
                    <div className="relative">
                      <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-400" />
                      <input
                        type="date"
                        value={formData.dateOfBirth}
                        onChange={(e) => setFormData({ ...formData, dateOfBirth: e.target.value })}
                        className="input-field pl-10"
                        data-testid="dob-input"
                      />
                    </div>
                  </div>
                </div>

                {/* Address Section */}
                <div className="pt-4 border-t border-neutral-200">
                  <h3 className="text-sm font-semibold text-neutral-700 mb-3 flex items-center gap-2">
                    <MapPin className="w-4 h-4" />
                    Address (Optional)
                  </h3>
                  <div className="space-y-3">
                    <input
                      type="text"
                      value={formData.street}
                      onChange={(e) => setFormData({ ...formData, street: e.target.value })}
                      className="input-field"
                      placeholder="Street Address"
                      data-testid="street-input"
                    />
                    <div className="grid grid-cols-3 gap-3">
                      <input
                        type="text"
                        value={formData.city}
                        onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                        className="input-field"
                        placeholder="City"
                        data-testid="city-input"
                      />
                      <input
                        type="text"
                        value={formData.state}
                        onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                        className="input-field"
                        placeholder="State"
                        data-testid="state-input"
                      />
                      <input
                        type="text"
                        value={formData.zipCode}
                        onChange={(e) => setFormData({ ...formData, zipCode: e.target.value })}
                        className="input-field"
                        placeholder="ZIP"
                        data-testid="zip-input"
                      />
                    </div>
                  </div>
                </div>

                {/* Emergency Contact Section */}
                <div className="pt-4 border-t border-neutral-200">
                  <h3 className="text-sm font-semibold text-neutral-700 mb-3 flex items-center gap-2">
                    <UserPlus className="w-4 h-4" />
                    Emergency Contact (Optional)
                  </h3>
                  <div className="space-y-3">
                    <input
                      type="text"
                      value={formData.emergencyName}
                      onChange={(e) => setFormData({ ...formData, emergencyName: e.target.value })}
                      className="input-field"
                      placeholder="Contact Name"
                      data-testid="emergency-name-input"
                    />
                    <div className="grid grid-cols-2 gap-3">
                      <input
                        type="tel"
                        value={formData.emergencyPhone}
                        onChange={(e) => setFormData({ ...formData, emergencyPhone: e.target.value })}
                        className="input-field"
                        placeholder="Phone Number"
                        data-testid="emergency-phone-input"
                      />
                      <input
                        type="text"
                        value={formData.emergencyRelationship}
                        onChange={(e) => setFormData({ ...formData, emergencyRelationship: e.target.value })}
                        className="input-field"
                        placeholder="Relationship"
                        data-testid="emergency-relationship-input"
                      />
                    </div>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="btn-primary w-full disabled:opacity-50 flex items-center justify-center gap-2"
                  data-testid="complete-signup-button"
                >
                  {isSubmitting && <Loader2 className="w-4 h-4 animate-spin" />}
                  {isSubmitting ? 'Creating Account...' : 'Create Account'}
                </button>
              </form>
            </>
          )}

          <p className="text-center text-neutral-400 text-xs mt-8">
            By creating an account, you agree to our{' '}
            <Link to="/terms" className="hover:text-neutral-600">Terms of Service</Link>
            {' '}and{' '}
            <Link to="/privacy" className="hover:text-neutral-600">Privacy Policy</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
